/**
 * `useRunLoader` — load the per-run `index.json` for a selected scan run.
 *
 * Given a selected run (`{ runDate, runId }`) or `null`, this hook fetches the
 * aggregator's per-run document
 * `dashboard/run-date=<runDate>/run-id=<runId>/index.json` (design Data flow §3)
 * and exposes a small, render-friendly state machine for the list view.
 *
 * Responsibilities (Req 1.3-1.5, 1.8, 2.1, 11.1-11.6, 12.4, 12.6):
 *
 * - Initiate loading immediately when a run is selected (Req 1.3, 11.1) — the
 *   effect fires synchronously on selection, well within the 1 s budget.
 * - Surface a `loading` status while in flight so the view can show a spinner
 *   and withhold partial data (Req 1.4, 11.2, 12.4). The previously loaded
 *   `index` is retained in state during the load and is never replaced until a
 *   newer load succeeds, so a failed load shows the prior results, not a blank.
 * - Abort an in-flight request when superseded by a newer selection, via
 *   `AbortController` — only the most recently selected run's data is applied
 *   (Req 1.5).
 * - Enforce a 30 s per-load timeout; on expiry the request is aborted and the
 *   load resolves to a `timeout` error (Req 11.2, 12.6).
 * - On full failure (network/HTTP/parse/timeout) retain the previously
 *   displayed `index` and surface an `error` identifying the affected run
 *   (Req 1.8, 11.4).
 * - Surface partial failure: when the index loads but the aggregator could not
 *   parse some repos, `unparseableCount` (= `index.unparseableRepoNames.length`)
 *   is non-zero while `status` is still `loaded` (Req 11.5, 10.5).
 * - Provide a `retry` control bounded at 3 attempts per selection (Req 11.6);
 *   `canRetry` reflects whether another attempt is allowed.
 *
 * The index document echoes the manifest's `expectedRepoCount`, `org`, and
 * `runDate` (design Data Models: `RunIndex`), so loading this single object also
 * satisfies the manifest-completeness inputs (Req 2.1); the comparison itself
 * lives in `lib/completeness.ts`.
 *
 * The hook is testable without a network: a `fetchFn`, `timeoutMs`,
 * `maxAttempts`, `buildUrl`, and `buildLibraryUrl` can all be injected; by
 * default it uses the global `fetch`, a 30 s timeout, 3 attempts, and the
 * canonical dashboard / library keys.
 *
 * ADDITIVE — Repo Knowledge Library (Req 18.2, 18.6): for the same selection
 * the hook also fetches the per-run Library document
 * `library/run-date=<runDate>/run-id=<runId>/dashboard-index.json`
 * (`DashboardLibraryIndex`), sharing the selection's `AbortController` so a
 * superseded/unmounted selection aborts it too. The Library load is issued once
 * the run `index.json` has loaded successfully so it never alters the index
 * load's failure, timeout, or retry behavior, and it is **purely additive and
 * degrades gracefully**: a 403/404/network/parse failure — or an older run with
 * no Library document at all — leaves `libraryIndex` `null` and never affects
 * the `index` load, its `status`, or its `error`. The existing
 * list/search/filter/sort/detail behavior is therefore unchanged when no
 * Library document exists; `libraryIndex` (and the derived `libraryByKey` map,
 * keyed via `lib/query.indexLibraryEntries`) is the only new state, and a
 * Library failure is silent — it never blocks or errors the list.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DashboardLibraryIndex, LibraryRepoEntry, RunIndex } from "../types";
import { indexLibraryEntries } from "../lib/query";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Per-load timeout: a run must load within 30 s or the attempt aborts (Req 11.2, 12.6). */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Maximum load attempts per selection, including the initial one (Req 11.6). */
export const DEFAULT_MAX_ATTEMPTS = 3;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** A selected scan run identified by its (run-date, run-id) pair. */
export interface RunSelection {
  runDate: string;
  runId: string;
}

/** The loader's lifecycle state for the current selection. */
export type RunLoaderStatus = "idle" | "loading" | "loaded" | "error";

/**
 * What went wrong on a failed load. Distinguishes the timeout case (Req 11.2,
 * 12.6) from network, HTTP-status, and malformed-body failures so the view can
 * tailor its message.
 */
export type RunLoaderErrorKind = "network" | "http" | "parse" | "timeout";

/** A full-failure error, always tagged with the run it pertains to (Req 11.4). */
export interface RunLoaderError {
  kind: RunLoaderErrorKind;
  message: string;
  /** The run-date this failure pertains to, for the affected-run message (Req 11.4). */
  runDate: string;
  /** The run-id this failure pertains to, for the affected-run message (Req 11.4). */
  runId: string;
}

/** The value returned by {@link useRunLoader}. */
export interface UseRunLoaderResult {
  /**
   * The currently displayable index. Retained across a failed reload so prior
   * results stay on screen (Req 1.8, 11.4); `null` until the first successful
   * load (or while idle with no selection).
   */
  index: RunIndex | null;
  /** Lifecycle state for the active selection (Req 1.4, 11.2, 12.4). */
  status: RunLoaderStatus;
  /** The full-failure error, or `null` when not in the `error` state (Req 11.4). */
  error: RunLoaderError | null;
  /** Number of successfully parsed repos in `index` (`summaries.length`), else 0. */
  loadedCount: number;
  /**
   * Number of repos the aggregator could not parse for the loaded run
   * (`unparseableRepoNames.length`); non-zero signals partial failure
   * (Req 11.5, 10.5).
   */
  unparseableCount: number;
  /** Load attempts spent on the current selection so far (Req 11.6). */
  attempts: number;
  /** Whether another retry is permitted: in `error` state and under the cap (Req 11.6). */
  canRetry: boolean;
  /** Re-initiate loading of the current selection; no-op unless {@link canRetry}. */
  retry: () => void;
  /**
   * ADDITIVE (Req 18.2, 18.6): the per-run Repo Knowledge Library document for
   * the current selection, or `null` when it is absent or failed to load. This
   * is purely additive state — a `null` value (an older run with no Library
   * artifact, or any 403/404/network/parse failure) never affects the `index`
   * load, `status`, or `error`; the list renders exactly as today.
   */
  libraryIndex: DashboardLibraryIndex | null;
  /**
   * ADDITIVE (Req 18.2): `libraryIndex` indexed by the composite
   * `repoName` + `scanId` join key (via `lib/query.indexLibraryEntries`) for
   * O(1) per-row enrichment lookup. An empty map when `libraryIndex` is `null`,
   * so summaries join to nothing and the view degrades gracefully.
   */
  libraryByKey: Map<string, LibraryRepoEntry>;
}

/** Injectable dependencies and tunables (all optional; sensible defaults). */
export interface UseRunLoaderOptions {
  /** Fetch implementation; defaults to the global `fetch`. Injected in tests. */
  fetchFn?: typeof fetch;
  /** Per-load timeout in ms; defaults to {@link DEFAULT_TIMEOUT_MS}. */
  timeoutMs?: number;
  /** Maximum attempts per selection; defaults to {@link DEFAULT_MAX_ATTEMPTS}. */
  maxAttempts?: number;
  /** Override the index URL builder; defaults to {@link buildIndexUrl}. */
  buildUrl?: (runDate: string, runId: string) => string;
  /**
   * ADDITIVE (Req 18.2): override the Library document URL builder; defaults to
   * {@link buildLibraryIndexUrl}. Injected in tests to exercise the additive
   * load and its graceful-absence behavior.
   */
  buildLibraryUrl?: (runDate: string, runId: string) => string;
}

// ---------------------------------------------------------------------------
// URL building
// ---------------------------------------------------------------------------

/**
 * Build the per-run index URL from a (run-date, run-id) pair, matching the
 * aggregator's output key `dashboard/run-date=<runDate>/run-id=<runId>/index.json`
 * (design Data flow §3). The path is root-absolute (leading slash) so it
 * resolves against the deployed origin regardless of the current client route
 * (e.g. `/run/:runDate/:runId`) and maps directly to the CloudFront
 * `dashboard/*` cache behavior. Both values are URL-encoded.
 */
export function buildIndexUrl(runDate: string, runId: string): string {
  const date = encodeURIComponent(runDate);
  const id = encodeURIComponent(runId);
  return `/dashboard/run-date=${date}/run-id=${id}/index.json`;
}

/**
 * ADDITIVE (Req 18.2): build the per-run Repo Knowledge Library document URL,
 * matching the Library's output key
 * `library/run-date=<runDate>/run-id=<runId>/dashboard-index.json` (design
 * "Dashboard integration (additive) — Req 18"). Mirrors {@link buildIndexUrl}:
 * the path is root-absolute so it resolves against the deployed origin
 * regardless of the current client route and maps to the CloudFront `library/*`
 * cache behavior; both values are URL-encoded.
 */
export function buildLibraryIndexUrl(runDate: string, runId: string): string {
  const date = encodeURIComponent(runDate);
  const id = encodeURIComponent(runId);
  return `/library/run-date=${date}/run-id=${id}/dashboard-index.json`;
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

interface InternalState {
  index: RunIndex | null;
  status: RunLoaderStatus;
  error: RunLoaderError | null;
  attempts: number;
}

const INITIAL_STATE: InternalState = {
  index: null,
  status: "idle",
  error: null,
  attempts: 0,
};

/**
 * Minimal structural guard: a valid index has the `summaries` and
 * `unparseableRepoNames` arrays the list view depends on. Anything else is
 * treated as a malformed body (`parse` failure) rather than rendered.
 */
function isRunIndex(value: unknown): value is RunIndex {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate.summaries) &&
    Array.isArray(candidate.unparseableRepoNames)
  );
}

/**
 * ADDITIVE (Req 18.2): structural guard for the Library document. Mirrors
 * {@link isRunIndex}: a valid `DashboardLibraryIndex` has the `repos` array the
 * join depends on plus the run-identifying string fields. Anything else is
 * treated as a malformed body and the Library load degrades to `null` (silent,
 * never failing the run's `index` load).
 */
function isDashboardLibraryIndex(
  value: unknown,
): value is DashboardLibraryIndex {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate.repos) &&
    typeof candidate.runId === "string" &&
    typeof candidate.runDate === "string"
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Load and track the per-run `index.json` for the given selection.
 *
 * @param selection - The run to load, or `null` for no selection (idle).
 * @param options - Injectable fetch/timeout/attempts/URL builder.
 * @returns The loader state and a bounded `retry` control.
 */
export function useRunLoader(
  selection: RunSelection | null,
  options: UseRunLoaderOptions = {},
): UseRunLoaderResult {
  const {
    fetchFn,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    buildUrl = buildIndexUrl,
    buildLibraryUrl = buildLibraryIndexUrl,
  } = options;

  const [state, setState] = useState<InternalState>(INITIAL_STATE);

  // ADDITIVE (Req 18.2): the per-run Library document, tracked entirely apart
  // from the run-index state machine above. `null` whenever it is absent or its
  // load failed; a Library failure never touches `state`.
  const [libraryIndex, setLibraryIndex] =
    useState<DashboardLibraryIndex | null>(null);

  // Bumping this re-runs the load effect for the same selection (retry path).
  const [reloadToken, setReloadToken] = useState(0);

  // Tracks the selection key across renders so we can reset the attempt counter
  // when the selection actually changes (vs. a retry of the same selection).
  const selectionKeyRef = useRef<string | null>(null);
  // Attempt counter for the current selection, kept in a ref so the effect can
  // read/update it without re-subscribing.
  const attemptsRef = useRef(0);

  const runDate = selection?.runDate ?? null;
  const runId = selection?.runId ?? null;

  useEffect(() => {
    // No selection: return to idle without disturbing any retained index.
    if (runDate === null || runId === null) {
      selectionKeyRef.current = null;
      attemptsRef.current = 0;
      setState((prev) => ({
        ...prev,
        status: "idle",
        error: null,
        attempts: 0,
      }));
      return;
    }

    // Reset the attempt counter when the selection changes; a retry of the same
    // selection (reloadToken bump) keeps counting up toward the cap.
    const key = `${runDate}\u0000${runId}`;
    if (selectionKeyRef.current !== key) {
      selectionKeyRef.current = key;
      attemptsRef.current = 0;
      // ADDITIVE (Req 18.2): drop any prior run's Library document so its stale
      // entries never linger across a selection change; the additive fetch
      // below repopulates it if this run has one.
      setLibraryIndex(null);
    }
    attemptsRef.current += 1;
    const attempt = attemptsRef.current;

    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    // Enter the loading state, retaining any prior index so a failure keeps it
    // on screen (Req 1.4/11.2: spinner; Req 1.8/11.4: retain prior results).
    setState((prev) => ({
      ...prev,
      status: "loading",
      error: null,
      attempts: attempt,
    }));

    const doFetch = fetchFn ?? fetch;

    void (async () => {
      try {
        const response = await doFetch(buildUrl(runDate, runId), {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new LoadFailure(
            "http",
            `Request for run ${runDate}/${runId} failed with status ${response.status}.`,
          );
        }

        let body: unknown;
        try {
          body = await response.json();
        } catch {
          throw new LoadFailure(
            "parse",
            `Run ${runDate}/${runId} returned a response that could not be parsed.`,
          );
        }

        if (!isRunIndex(body)) {
          throw new LoadFailure(
            "parse",
            `Run ${runDate}/${runId} returned an unexpected index shape.`,
          );
        }

        clearTimeout(timeoutId);
        // Success replaces the displayed index (Req 11.3). Partial failure is
        // carried inside the index via unparseableRepoNames (Req 11.5).
        setState({
          index: body,
          status: "loaded",
          error: null,
          attempts: attempt,
        });

        // ADDITIVE (Req 18.2, 18.6): once the run index has loaded, fetch the
        // per-run Library document for the SAME selection, sharing this
        // selection's controller so a supersede/unmount aborts it too. It is
        // gated on index success so it never alters the index load's failure,
        // timeout, or retry behavior (existing behavior is unchanged), and it
        // is fully isolated — it only ever sets `libraryIndex` and never
        // touches `state`. ANY failure (HTTP 403/404, network, parse,
        // unexpected shape, or supersede) is swallowed, leaving `libraryIndex`
        // as `null` so older runs with no Library document render exactly as
        // today (graceful absence).
        void (async () => {
          try {
            const libResponse = await doFetch(buildLibraryUrl(runDate, runId), {
              signal: controller.signal,
            });
            if (!libResponse.ok) {
              return; // absent/forbidden Library document — silently skip.
            }
            const libBody: unknown = await libResponse.json();
            if (!isDashboardLibraryIndex(libBody)) {
              return; // malformed Library document — silently skip.
            }
            // Drop the result if this selection was superseded/aborted in
            // flight so only the current selection's document is applied.
            if (controller.signal.aborted) {
              return;
            }
            setLibraryIndex(libBody);
          } catch {
            // Purely additive: keep `libraryIndex` null, never surface an error.
          }
        })();
      } catch (caught) {
        clearTimeout(timeoutId);

        // Superseded by a newer selection (cleanup aborted us): drop silently —
        // the newer effect run owns the state now (Req 1.5).
        if (controller.signal.aborted && !timedOut) {
          return;
        }

        const error = toRunLoaderError(caught, timedOut, runDate, runId);
        // Retain the previously displayed index; only flip to error (Req 1.8, 11.4).
        setState((prev) => ({
          index: prev.index,
          status: "error",
          error,
          attempts: attempt,
        }));
      }
    })();

    // Cleanup on supersede/unmount: cancel the in-flight request (Req 1.5).
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [runDate, runId, reloadToken, fetchFn, timeoutMs, buildUrl, buildLibraryUrl]);

  const canRetry = state.status === "error" && state.attempts < maxAttempts;

  const retry = useCallback(() => {
    // Only re-initiate while in a failed state and under the attempt cap (Req 11.6).
    setReloadToken((token) => token + 1);
  }, []);

  const loadedCount = state.index?.summaries.length ?? 0;
  const unparseableCount = state.index?.unparseableRepoNames.length ?? 0;

  // ADDITIVE (Req 18.2): derive the join-key index once per Library document so
  // the view can enrich rows in O(1); empty when no Library document is loaded.
  const libraryByKey = useMemo(
    () => indexLibraryEntries(libraryIndex),
    [libraryIndex],
  );

  return {
    index: state.index,
    status: state.status,
    error: state.error,
    loadedCount,
    unparseableCount,
    attempts: state.attempts,
    canRetry,
    retry: canRetry ? retry : NO_OP,
    libraryIndex,
    libraryByKey,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const NO_OP = (): void => {};

/** Internal carrier for a classified load failure thrown inside the async load. */
class LoadFailure extends Error {
  constructor(
    readonly kind: Exclude<RunLoaderErrorKind, "network">,
    message: string,
  ) {
    super(message);
    this.name = "LoadFailure";
  }
}

/**
 * Map a thrown value (or timeout flag) to a {@link RunLoaderError}, tagging it
 * with the affected run so the view can name it (Req 11.4).
 */
function toRunLoaderError(
  caught: unknown,
  timedOut: boolean,
  runDate: string,
  runId: string,
): RunLoaderError {
  if (timedOut) {
    return {
      kind: "timeout",
      message: `Loading run ${runDate}/${runId} timed out.`,
      runDate,
      runId,
    };
  }
  if (caught instanceof LoadFailure) {
    return { kind: caught.kind, message: caught.message, runDate, runId };
  }
  // Anything else (e.g. a network error from fetch) is a network failure.
  const detail = caught instanceof Error ? caught.message : String(caught);
  return {
    kind: "network",
    message: `Could not load run ${runDate}/${runId}: ${detail}`,
    runDate,
    runId,
  };
}
